<script setup>
import { computed, ref, onMounted } from 'vue';
import dayjs from 'dayjs';
import SmartwelResponsiveSheet from './SmartwelResponsiveSheet.vue';
import { createOrder, getProduct } from '@/api/PaymentApi';
import { SMARTWEL_SUBSCRIPTION_PRODUCT_ID } from '@/constants/partner';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { usePaymentsStore } from '@/store/modules/payments';

/**
 * 복지찬스 정기구독 결제 바텀시트.
 * - SMARTWEL SUBSCRIPTION 상품을 createOrder 로 구매 → 백엔드가 구독 계약 생성
 *   (상품 ID는 환경별 분기: 개발=p_test_sub_smartwel01, 운영=p_sub_smartwel01 — @/constants/partner)
 * - SMARTWEL 결제: amount 에 복지P 금액, pointAmount 0 (서버가 복지P 직접 차감) — exchange.vue 패턴과 동일
 * - 가격은 상품(productId)의 실제 price 를 조회해 사용 (하드코딩 금액 불일치 방지)
 * - 약관 동의 시에만 결제 버튼 활성화
 */
const PRODUCT_ID = SMARTWEL_SUBSCRIPTION_PRODUCT_ID;

const emit = defineEmits(['close', 'pay']);
const { partner } = useMember();
const myInfoStore = useMyInfoStore();
const paymentsStore = usePaymentsStore();

/** 상품 실제 가격(복지P). 마운트 시 상품 조회로 채움 (0이면 미로드) */
const monthlyPrice = ref(0);
onMounted(async () => {
  try {
    const product = await getProduct(PRODUCT_ID);
    monthlyPrice.value = product?.price ?? 0;
  } catch (e) {
    monthlyPrice.value = 0;
  }
});

const balance = computed(() => partner.value?.totalPoint ?? 0);
const isShortage = computed(() => monthlyPrice.value > 0 && balance.value < monthlyPrice.value);
const shortageAmount = computed(() => Math.max(monthlyPrice.value - balance.value, 0));
const priceText = computed(() => monthlyPrice.value.toLocaleString());
const balanceNum = computed(() => balance.value.toLocaleString());
const shortageText = computed(() => `${shortageAmount.value.toLocaleString()}P`);
const nextPaymentDate = computed(() => dayjs().add(1, 'month').format('YYYY년 M월 D일'));

const agreed = ref(false);
const showTermsDetail = ref(false);
const isPaying = ref(false);

const toggleAgree = () => {
  agreed.value = !agreed.value;
};
const toggleTermsDetail = () => {
  showTermsDetail.value = !showTermsDetail.value;
};

const onPay = async () => {
  if (!agreed.value || isPaying.value || !monthlyPrice.value) return;
  isPaying.value = true;
  try {
    await createOrder({
      amount: monthlyPrice.value, // SMARTWEL: 복지P 금액 (상품 실제 가격, 서버가 복지P 직접 차감)
      pointAmount: 0,
      totalAmount: monthlyPrice.value,
      productId: PRODUCT_ID,
      orderName: '낭만만세력 정기구독',
    });
    await myInfoStore.fetchMyInfo(); // 복지P 잔액 갱신
    await paymentsStore.fetchActiveSubscriptions(true); // 구독 상태 갱신 → 진입점 버튼 구독중으로
    emit('pay'); // 부모: 결제 시트 닫고 "구독이 시작되었어요!" 토스트
  } catch (e) {
    const msg = e?.response?.data?.detail || '결제 중 오류가 발생했습니다. 다시 시도해주세요.';
    alert(msg);
  } finally {
    isPaying.value = false;
  }
};
</script>

<template>
  <SmartwelResponsiveSheet @close="emit('close')">
    <div class="sub-header">
      <h2 class="sub-header__title">낭만만세력 구독</h2>
      <div class="sub-step">
        <span class="sub-step__dot active" />
        <span class="sub-step__dot active" />
        <span class="sub-step__count">2/2</span>
      </div>
      <button type="button" class="sub-close" aria-label="닫기" @click="emit('close')"></button>
    </div>

    <div class="sheet-body">
      <!-- 결제 정보 카드 -->
      <div class="payment-card">
        <span class="plan-tag">복지찬스 회원 전용 특가</span>
        <div class="plan-name">월간 구독</div>
        <div class="plan-price-row">
          <span class="plan-price-num">{{ priceText }}</span>
          <span class="plan-price-unit">원 / 월</span>
        </div>
        <div class="plan-info-row">
          <span class="plan-info-label">다음 결제일</span>
          <span class="plan-info-value">{{ nextPaymentDate }}</span>
        </div>
      </div>

      <!-- 결제 수단 -->
      <div class="payment-method">
        <div class="method-row">
          <div class="method-icon">💳</div>
          <div class="method-text">
            <div class="method-label">복지포인트로 자동 결제</div>
            <div class="method-sub">매월 결제일에 자동 차감됩니다</div>
          </div>
        </div>
        <div class="method-balance">
          <span class="method-balance-label">
            <span class="method-balance-pbadge">P</span>
            보유 복지포인트
          </span>
          <span class="method-balance-value" :class="{ shortage: isShortage }">
            {{ balanceNum }}<span class="method-balance-unit">P</span>
          </span>
        </div>
      </div>

      <!-- 잔액 충분 안내 -->
      <div v-if="!isShortage" class="normal-notice">
        <span class="icon">ℹ️</span>
        <span class="text">
          결제일에 포인트가 부족하면 다음 입금분에서 자동으로 차감되어, 서비스는 끊김 없이 이용할 수
          있어요
        </span>
      </div>

      <!-- 잔액 부족 안내 -->
      <div v-else class="shortage-notice">
        <span class="icon">⚠️</span>
        <span class="text">
          현재 잔액이 부족하지만 정기구독은 가능해요.
          <strong>부족분 {{ shortageText }}는 다음 포인트 입금일에 자동 차감</strong>되며, 서비스는
          즉시 이용할 수 있어요
        </span>
      </div>

      <!-- 약관 동의 -->
      <div class="terms-area">
        <div class="terms-row" @click="toggleAgree">
          <div class="terms-checkbox" :class="{ checked: agreed }">
            <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <div class="terms-text">
            <span class="required">[필수]</span>정기결제 및 자동 차감에 동의합니다
          </div>
          <span class="terms-detail-btn" @click.stop="toggleTermsDetail">보기</span>
        </div>
        <div v-if="showTermsDetail" class="terms-detail">
          <ul>
            <li>매월 결제일에 복지포인트에서 자동 결제됩니다</li>
            <li>결제일에 포인트가 부족하면 다음 입금분에서 자동 차감됩니다</li>
            <li>언제든 마이페이지에서 구독을 해지할 수 있어요</li>
            <li>해지 시 다음 결제일까지 서비스 이용 가능합니다</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="cta-area">
      <button
        type="button"
        class="cta-btn"
        :disabled="!agreed || isPaying || !monthlyPrice"
        @click="onPay"
      >
        {{ isPaying ? '결제 중…' : `${priceText}원 결제하기` }}
      </button>
      <div class="cta-hint">결제 즉시 모든 서비스를 이용할 수 있어요</div>
    </div>
  </SmartwelResponsiveSheet>
</template>

<style lang="scss" scoped>
/* 헤더 — 1단계 권유 시트(SmartwelUpsellSheet)와 동일 스타일 */
.sub-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 16px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
}

/* 모바일 바텀시트: 헤더 좌우 16px */
.srs-sheet--mobile .sub-header {
  padding: 16px;
}

.sub-header__title {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 800;
  color: #1f2937;
}

.sub-step {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  margin-left: auto;
  margin-right: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
}

.sub-step__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d1d5db;

  &.active {
    background: #1e3a5f;
  }
}

.sub-step__count {
  margin-left: 2px;
}

.sub-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: url('@manseImages/icon_close_1.svg') no-repeat center / contain;
  cursor: pointer;
}

.sheet-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;

  &::-webkit-scrollbar {
    width: 0;
  }
}

/* 결제 정보 카드 */
.payment-card {
  background: linear-gradient(160deg, #1e3a5f 0%, #2c4a75 100%);
  border-radius: 14px;
  padding: 16px 16px 14px;
  color: #fff;
  margin-bottom: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    right: -20px;
    width: 80px;
    height: 80px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%);
    border-radius: 50%;
  }
}

.plan-tag {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
}

.plan-name {
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}

.plan-price-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 12px;
  position: relative;
  z-index: 1;
}

.plan-price-num {
  font-size: 26px;
  font-weight: 700;
  line-height: 1;
}

.plan-price-unit {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.plan-info-row {
  display: flex;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
  z-index: 1;
}

.plan-info-label {
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.7);
}

.plan-info-value {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}

/* 결제 수단 */
.payment-method {
  background: #f9fafb;
  border-radius: 12px;
  padding: 14px 14px 12px;
  margin-bottom: 10px;
}

.method-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.method-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
}

.method-text {
  flex: 1;
}

.method-label {
  font-size: 13.5px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 2px;
}

.method-sub {
  font-size: 11.5px;
  color: #6b7280;
}

.method-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.method-balance-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13.5px;
  color: #6b7280;
  font-weight: 500;
}

/* 앱 전반 복지포인트 표기와 동일한 파란 원형 P 뱃지 (SmartwelEntryRow .swr__p-badge 와 동일) */
.method-balance-pbadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--smartwel-blue, #3391ff);
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}

.method-balance-value {
  font-size: 15px;
  font-weight: 700;
  color: var(--smartwel-blue, #3391ff);

  &.shortage {
    color: #dc2626;
  }
}

/* 단위 P — 숫자보다 작고 가는 검정 (SmartwelEntryRow .swr__point-unit 톤) */
.method-balance-unit {
  margin-left: 1px;
  font-size: 12px;
  font-weight: 400;
  color: #333;
}

/* 잔액 부족 안내 */
.shortage-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  background: #fef3e2;
  border-radius: 10px;
  margin-bottom: 14px;
  border-left: 3px solid #f59e0b;

  .icon {
    font-size: 16px;
    flex-shrink: 0;
    line-height: 1.3;
  }

  .text {
    font-size: 12px;
    color: #92400e;
    line-height: 1.5;
    font-weight: 500;

    strong {
      color: #78350f;
      font-weight: 700;
    }
  }
}

/* 잔액 충분 안내 */
.normal-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 11px 14px;
  background: #f0f9ff;
  border-radius: 10px;
  margin-bottom: 14px;
  border-left: 3px solid #0ea5e9;

  .icon {
    font-size: 14px;
    flex-shrink: 0;
    line-height: 1.4;
  }

  .text {
    font-size: 13px;
    color: #075985;
    line-height: 1.5;
    font-weight: 500;
  }
}

/* 약관 */
.terms-area {
  border-top: 1px solid #f3f4f6;
  padding: 14px 0 4px;
  margin-bottom: 8px;
}

.terms-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 2px;
  cursor: pointer;
}

.terms-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid #d1d5db;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;

  svg {
    width: 12px;
    height: 12px;
    stroke: #fff;
    stroke-width: 3;
    fill: none;
    opacity: 0;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &.checked {
    background: #1e3a5f;
    border-color: #1e3a5f;

    svg {
      opacity: 1;
    }
  }
}

.terms-text {
  flex: 1;
  font-size: 13.5px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;

  .required {
    color: #dc2626;
    font-weight: 600;
    margin-right: 2px;
  }
}

.terms-detail-btn {
  font-size: 12px;
  color: #6b7280;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  flex-shrink: 0;
  font-weight: 500;
}

.terms-detail {
  margin-top: 10px;
  padding: 12px 14px;
  background: #f9fafb;
  border-radius: 10px;
  font-size: 11.5px;
  color: #4b5563;
  line-height: 1.6;

  ul {
    list-style: none;
    padding: 0;
  }

  li {
    padding-left: 12px;
    position: relative;
    margin-bottom: 4px;

    &::before {
      content: '•';
      position: absolute;
      left: 2px;
      color: #1e3a5f;
    }
  }
}

/* CTA */
.cta-area {
  padding: 12px 20px 18px;
  border-top: 1px solid #f3f4f6;
  background: #fff;
  flex-shrink: 0;
}

.cta-btn {
  width: 100%;
  background: #1e3a5f;
  color: #fff;
  border: none;
  padding: 15px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #2c4a75;
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
}

.cta-hint {
  font-size: 12.5px;
  color: #9ca3af;
  text-align: center;
  margin-top: 8px;
}

/* PC 팝업: body 좌우 32px, CTA 좌우·하단 32px (헤더 제외) */
.srs-sheet--pc .sheet-body {
  padding-left: 32px;
  padding-right: 32px;
}
.srs-sheet--pc .cta-area {
  padding-left: 32px;
  padding-right: 32px;
  padding-bottom: 32px;
}
</style>
